import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { NoticeFontFile } from './notice-fidelity.models';
import { NoticeFidelityService } from './notice-fidelity.service';

const family = 'PHMS Notice Lato';
const maxFaceBytes = 1024 * 1024;
const expectedFaces = [
  ['lato-regular', 'Lato-Regular.ttf', '400', 'normal'],
  ['lato-bold', 'Lato-Bold.ttf', '700', 'normal'],
  ['lato-italic', 'Lato-Italic.ttf', '400', 'italic'],
  ['lato-bold-italic', 'Lato-BoldItalic.ttf', '700', 'italic'],
] as const;

// Some DOM library versions omit the mutable FontFaceSet methods even though
// supported browsers expose them. Check both methods before using this seam.
interface NoticeFontCollection {
  add(face: FontFace): unknown;
  delete(face: FontFace): unknown;
}

export class NoticeFontAccessError extends Error {
  constructor(readonly status: 401 | 403) {
    super('Notice font access changed. Refresh access before continuing.');
    this.name = 'NoticeFontAccessError';
  }
}

@Injectable({ providedIn: 'root' })
export class NoticeFontLoaderService {
  private readonly api = inject(NoticeFidelityService);
  private readonly document = inject(DOCUMENT);
  private cached: { identity: string; promise: Promise<string> } | null = null;

  async load(files: readonly NoticeFontFile[]): Promise<string> {
    const ordered = this.descriptors(files);
    const identity = JSON.stringify(ordered);
    if (this.cached) {
      if (this.cached.identity !== identity)
        throw new Error(
          'The notice font contract changed. Reload the page before using Page editing.',
        );
      return this.cached.promise;
    }
    const fontCollection = this.document.fonts as unknown as
      | Partial<NoticeFontCollection>
      | undefined;
    if (
      typeof FontFace !== 'function' ||
      !fontCollection ||
      typeof fontCollection.add !== 'function' ||
      typeof fontCollection.delete !== 'function' ||
      !globalThis.crypto?.subtle
    )
      throw new Error(
        'This browser cannot verify and load the notice fonts. Use Fields editing.',
      );

    const promise = this.loadVerified(
      ordered,
      fontCollection as NoticeFontCollection,
    );
    const cached = { identity, promise };
    this.cached = cached;
    try {
      return await promise;
    } catch (error) {
      if (this.cached === cached) this.cached = null;
      throw error;
    }
  }

  private descriptors(files: readonly NoticeFontFile[]): NoticeFontFile[] {
    if (!Array.isArray(files) || files.length !== expectedFaces.length)
      throw new Error(
        'The notice font contract is incomplete. Use Fields editing.',
      );
    return expectedFaces.map(([code, fileName, weight, style]) => {
      const matches = files.filter((file) => file?.code === code);
      const file = matches[0];
      if (
        matches.length !== 1 ||
        file.fileName !== fileName ||
        file.weight !== weight ||
        file.style !== style ||
        typeof file.sha256 !== 'string' ||
        !/^[a-f0-9]{64}$/.test(file.sha256)
      )
        throw new Error(
          'The notice font contract does not match the supported faces. Use Fields editing.',
        );
      return { code, fileName, weight, style, sha256: file.sha256 };
    });
  }

  private async loadVerified(
    files: NoticeFontFile[],
    fontCollection: NoticeFontCollection,
  ): Promise<string> {
    const added: FontFace[] = [];
    try {
      const bytes = await Promise.all(
        files.map(async (file) => {
          const buffer = await firstValueFrom(this.api.font(file.code));
          if (
            !(buffer instanceof ArrayBuffer) ||
            buffer.byteLength === 0 ||
            buffer.byteLength > maxFaceBytes
          )
            throw new Error('Invalid notice font size.');
          const digest = await globalThis.crypto.subtle.digest(
            'SHA-256',
            buffer,
          );
          const hash = [...new Uint8Array(digest)]
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
          if (hash !== file.sha256)
            throw new Error('Notice font verification failed.');
          return buffer;
        }),
      );
      const faces = files.map(
        (file, index) =>
          new FontFace(family, bytes[index], {
            weight: file.weight,
            style: file.style,
            display: 'block',
          }),
      );
      await Promise.all(faces.map((face) => face.load()));
      for (const face of faces) {
        added.push(face);
        fontCollection.add(face);
      }
      return family;
    } catch (error) {
      for (const face of added) fontCollection.delete(face);
      if (
        error instanceof HttpErrorResponse &&
        (error.status === 401 || error.status === 403)
      )
        throw new NoticeFontAccessError(error.status);
      throw new Error(
        'The exact notice fonts could not be verified and loaded. Use Fields editing or retry Page editing.',
      );
    }
  }
}
